import type { SemanticSearchResult } from '../types';

interface SemanticSearchResultsProps {
  query: string;
  results: SemanticSearchResult[];
  isLoading: boolean;
  error: string | null;
  onOpenContact: (contactId: number) => void;
}

const sourceLabel: Record<SemanticSearchResult['source_type'], string> = {
  conversation: 'Conversation',
  profile: 'Profile',
  facts: 'Facts',
  transcript: 'Transcript',
  image_description: 'Image',
};

export function SemanticSearchResults({
  query,
  results,
  isLoading,
  error,
  onOpenContact,
}: SemanticSearchResultsProps) {
  if (!query.trim()) return null;

  return (
    <div className="px-4 pt-4 pb-1">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-ink">Semantic Matches</h3>
        {!isLoading && !error && (
          <span className="text-xs text-ink-secondary">{results.length} results</span>
        )}
      </div>

      {isLoading && (
        <div className="rounded-xl border border-edge bg-white px-4 py-4 text-sm text-ink-secondary">
          Searching conversation memory...
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!isLoading && !error && results.length === 0 && (
        <div className="rounded-xl border border-edge bg-white px-4 py-4 text-sm text-ink-secondary">
          No semantic matches yet.
        </div>
      )}

      {!isLoading && !error && results.length > 0 && (
        <div className="space-y-2">
          {results.map((result) => {
            const canOpen = result.contact_id !== null;
            const similarityPct = Math.round(result.similarity * 100);

            return (
              <button
                key={result.chunk_key}
                onClick={() => canOpen && onOpenContact(result.contact_id as number)}
                disabled={!canOpen}
                className="w-full text-left rounded-xl border border-edge bg-white p-3 hover:bg-canvas-secondary transition-colors disabled:cursor-not-allowed disabled:opacity-70"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-sm font-semibold text-ink truncate">
                    {result.contact_name || result.contact_phone}
                  </p>
                  <span className="text-xs text-ink-secondary">{similarityPct}%</span>
                </div>

                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-canvas border border-edge text-ink-secondary">
                    {sourceLabel[result.source_type]}
                  </span>
                  {result.chunk_date && (
                    <span className="text-[11px] text-ink-tertiary">{result.chunk_date}</span>
                  )}
                </div>

                <p className="text-sm text-ink-secondary line-clamp-3">{result.content}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SemanticSearchResults;
